/**
 * How the engine reaches the person: through the interface that shows it
 * (design note docs/plans/2026-10-19-engine-interface-separation.md §3.3,
 * step 2). Engine code never opens a toast, a dialog or a progress card
 * itself; it calls these, and the interface registers how each is shown
 * (`setHost`). Without an interface (tests, a headless use) each has a quiet
 * default.
 *
 *   notify(notice)          something to know: { code, level, message,
 *                           data, duration }; every "notice" listener gets it
 *   ask(question)           a question the engine waits for: { code, data,
 *                           fallback } -> the answer (fallback without a host)
 *   runOperation(title, work, describeResult)
 *                           a long operation with progress: work(progress),
 *                           progress.update(…) as interfaces/classic/ui/operation-progress.js
 *   beginOperation(title)   the same, driven by hand: update, paint,
 *                           finish, fail, dismiss
 *   beginBlockingOperation(texts)
 *                           an operation the person must wait for (the page
 *                           held, leaving warned): { title, intro, preparing,
 *                           done, failed } -> { paint(), update(current,
 *                           total), finish(success) }
 *   newItemColor(usedColors)
 *                           a colour for an item placed at the top level
 */
import { emit, Events } from "./core/events.js";

// Colours used without an interface (the light theme's).
const DEFAULT_COLORS = ["#4f46e5", "#0891b2", "#059669", "#d97706", "#dc2626", "#7c3aed", "#db2777", "#2563eb"];

const handlers = {
  ask: null,
  runOperation: null,
  beginOperation: null,
  beginBlockingOperation: null,
  newItemColor: null,
};

const quietProgress = () => ({ update() {}, finish() {}, fail() {}, cancelled() {}, dismiss() {}, setCancellable() {}, async paint() {} });

/** The interface registers how it shows what the engine needs to show. */
export function setHost(host) {
  for (const key of Object.keys(handlers)) if (typeof host?.[key] === "function") handlers[key] = host[key];
}

/**
 * Something the person should know. Every listener of the engine's "notice"
 * event gets it (the interface shows `message`, in the person's language).
 * @param {{code: string, level?: "info"|"success"|"warning"|"error", message?: string, data?: Object, duration?: number}} notice
 */
export function notify(notice) {
  emit(Events.ENGINE_NOTICE, { level: "info", data: {}, ...notice });
}

/**
 * Ask the person and wait for the answer; `fallback` when no interface
 * answers.
 * @param {{code: string, data?: Object, fallback?: *}} question
 */
export async function ask(question) {
  if (!handlers.ask) return question.fallback;
  return handlers.ask(question);
}

/** A long operation with its progress shown by the interface. */
export function runOperation(title, work, describeResult) {
  if (handlers.runOperation) return handlers.runOperation(title, work, describeResult);
  return work(quietProgress());
}

/** A long operation's progress, driven by the caller. */
export function beginOperation(title, options) {
  return handlers.beginOperation ? handlers.beginOperation(title, options) : quietProgress();
}

/** An operation the person must wait for: { paint(), update(current, total), finish(success) }. */
export function beginBlockingOperation(texts) {
  return handlers.beginBlockingOperation ? handlers.beginBlockingOperation(texts) : { async paint() {}, update() {}, finish() {} };
}

/** A colour not used by `usedColors`, for an item placed at the top level. */
export function newItemColor(usedColors = []) {
  if (handlers.newItemColor) return handlers.newItemColor(usedColors);
  return DEFAULT_COLORS.find((color) => !usedColors.includes(color)) || DEFAULT_COLORS[0];
}
